import { useMemo, useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, Stack,
  TextField, Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import {
  useAnforderungen, useCreateAnforderung, useUpdateAnforderung,
  useDeleteAnforderung, useReorderAnforderungen,
} from '../../../api/hooks/useAnforderungen';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useProject } from '../../../api/hooks/useProjects';
import { useAnforderungKategorieNamen } from '../../../api/hooks/useAnforderungKategorien';
import { useNotifications } from '../../../context/NotificationContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { AnforderungDialog } from '../components/AnforderungDialog';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildAnforderungenReport } from '../reports/anforderungenReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { PmCard, PmColumnPicker, PmHeadCell, useTableSortFilter, handleInlineEditKeyDown, type SortFilterColumn } from '../../../components/pm';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';

interface Anforderung {
  id: string;
  nr: string | null;
  role: string | null;
  ziel: string;
  nutzen: string | null;
  kategorie: string | null;
  story_points: number | null;
  status: 'Neu' | 'In Planung' | 'Umgesetzt' | 'Abgenommen';
}

const STATUSES = ['Neu', 'In Planung', 'Umgesetzt', 'Abgenommen'] as const;


/** Stabiler Sortier-Schluessel: numerische nr zuerst (aufsteigend), Rest ans Ende. */
function nrKey(a: Anforderung): number {
  const n = Number(a.nr);
  return a.nr != null && a.nr.trim() !== '' && !Number.isNaN(n) ? n : Number.POSITIVE_INFINITY;
}

interface CoreFields {
  role: string;
  ziel: string;
  kategorie: string;
  story_points: string;
  status: Anforderung['status'];
}

function coreFrom(a: Anforderung): CoreFields {
  return {
    role: a.role ?? '', ziel: a.ziel, kategorie: a.kategorie ?? '',
    story_points: a.story_points == null ? '' : String(a.story_points), status: a.status,
  };
}

function toPayload(c: CoreFields): Record<string, unknown> {
  return {
    role: c.role.trim() || null, ziel: c.ziel.trim(),
    kategorie: c.kategorie || null,
    story_points: c.story_points.trim() === '' ? null : Number(c.story_points),
    status: c.status,
  };
}

export function AnforderungenTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const { data, isLoading } = useAnforderungen(projectId);
  const createMut = useCreateAnforderung(projectId);
  const updateMut = useUpdateAnforderung(projectId);
  const deleteMutation = useDeleteAnforderung(projectId);
  const reorderMut = useReorderAnforderungen(projectId);
  const items = ((data as unknown as { data?: Anforderung[] } | undefined)?.data ?? []);
  useSyncMaturityFromCount(projectId, 8, isLoading ? -1 : items.length);

  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  // Mandantenspezifische Kategorien (statt hartcodierter Liste).
  const kategorien = useAnforderungKategorieNamen();

  const [editItem, setEditItem] = useState<Anforderung | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Basis-Reihenfolge = nach Positionsnummer (nr) sortiert. Die angezeigte Nr.
  // ergibt sich aus dieser Position und ist nicht direkt editierbar.
  const baseOrder = useMemo(
    () => [...items].sort((a, b) => nrKey(a) - nrKey(b)),
    [items],
  );
  const posById = useMemo(() => {
    const m = new Map<string, number>();
    baseOrder.forEach((a, i) => m.set(a.id, i + 1));
    return m;
  }, [baseOrder]);

  const statusLabel = (s: Anforderung['status']) => t(`anforderungen.statuses.${s}`, s);

  const columns = useMemo<SortFilterColumn<Anforderung>[]>(() => [
    { key: 'nr', label: t('anforderungen.nr', 'Nr.'), align: 'right', hideable: false, value: (r) => posById.get(r.id) ?? 0 },
    {
      key: 'role', label: t('anforderungen.role', 'Rolle'),
      filter: { type: 'text' }, value: (r) => r.role ?? '',
    },
    {
      key: 'ziel', label: t('anforderungen.ziel', 'Ziel'),
      filter: { type: 'text' }, value: (r) => r.ziel,
    },
    {
      key: 'kategorie', label: t('anforderungen.kategorie', 'Kategorie'),
      filter: { type: 'enum', options: kategorien.map((k) => ({ value: k, label: k })) },
      value: (r) => r.kategorie ?? '',
    },
    { key: 'story_points', label: t('anforderungen.storyPoints', 'SP'), align: 'right', defaultHidden: true, value: (r) => r.story_points ?? null },
    {
      key: 'status', label: t('fields.state', 'Status'), defaultHidden: true,
      filter: { type: 'enum', options: STATUSES.map((s) => ({ value: s, label: statusLabel(s) })) },
      value: (r) => r.status,
    },
  ], [posById, t, kategorien]);

  const sf = useTableSortFilter(baseOrder, columns, { tableId: 'anforderungen' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !sf.isManualOrder) return;
    const ids = baseOrder.map((r) => r.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newIds = arrayMove(ids, oldIndex, newIndex);
    const currentNr = new Map(baseOrder.map((r) => [r.id, r.nr]));
    const updates = newIds
      .map((id, i) => ({ id, nr: i + 1 }))
      .filter((u) => currentNr.get(u.id) !== String(u.nr));
    if (updates.length === 0) return;
    reorderMut.mutate(updates, {
      onError: (err) => notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'),
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMutation.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  const head = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('anforderungen.title', 'Anforderungen') };
  const buildReport = () =>
    buildAnforderungenReport({
      items: baseOrder.map((a) => ({
        nr: String(posById.get(a.id) ?? ''), role: a.role, ziel: a.ziel, kategorie: a.kategorie,
        story_points: a.story_points, status: a.status,
      })),
    });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('anforderungen.title', 'Anforderungen')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <PmColumnPicker columns={sf.hideableColumns} isVisible={sf.isVisible} onToggle={sf.toggleColumn} />
          <PdfExportButton meta={pdfMeta} build={buildReport} />
        </Stack>
      </Stack>

      <PmCard flush>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--pm-bg-muted)' }}>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            {sf.isManualOrder
              ? t('anforderungen.dragHint', 'Reihenfolge per Ziehen (⠿) festlegen — die Nr. ergibt sich aus der Position. Oben direkt eintragen, Enter genügt.')
              : t('anforderungen.sortActiveHint', 'Sortierung/Filter aktiv — die Reihenfolge ist nur in der Standardansicht (ohne Sortierung/Filter) per Drag&Drop änderbar.')}
          </Typography>
        </Box>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...head, width: 36 }} />
                {sf.visibleColumns.map((col) => (
                  <PmHeadCell
                    key={col.key}
                    label={col.label}
                    align={col.align}
                    filter={col.filter}
                    sortActive={sf.sortKey === col.key}
                    sortDir={sf.sortDir}
                    onToggleSort={() => sf.toggleSort(col.key)}
                    filterValue={sf.filters[col.key]}
                    onSetFilter={(v) => sf.setFilter(col.key, v)}
                    onClearFilter={() => sf.clearFilter(col.key)}
                    headSx={head}
                  />
                ))}
                <TableCell sx={head} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <AddAnforderungRow
                statusLabel={statusLabel}
                nextNr={baseOrder.length + 1}
                isVisible={sf.isVisible}
                kategorien={kategorien}
                onCreate={async (core) => {
                  await createMut.mutateAsync({ ...toPayload(core), nr: String(baseOrder.length + 1), nutzen: null });
                }}
              />
              <SortableContext items={sf.rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                {sf.rows.map((item) => (
                  <AnforderungRow
                    key={item.id}
                    item={item}
                    pos={posById.get(item.id) ?? 0}
                    dragDisabled={!sf.isManualOrder}
                    isVisible={sf.isVisible}
                    kategorien={kategorien}
                    statusLabel={statusLabel}
                    onSave={async (core) => { await updateMut.mutateAsync({ id: item.id, data: { ...toPayload(core), nutzen: item.nutzen } }); }}
                    onEditDetail={() => setEditItem(item)}
                    onDelete={() => setDeleteId(item.id)}
                  />
                ))}
              </SortableContext>
              {sf.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={sf.visibleColumns.length + 2}>
                    <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                      {items.length === 0
                        ? t('anforderungen.emptyHint', 'Lege die erste User Story an um zu starten.')
                        : t('anforderungen.filterEmpty', 'Keine Treffer fuer die aktuelle Filter-Kombination.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </PmCard>

      <AnforderungDialog
        open={!!editItem}
        mode="edit"
        projectId={projectId}
        initialData={editItem ? (editItem as unknown as Record<string, unknown>) : undefined}
        onClose={() => setEditItem(null)}
      />

      <ConfirmDialog
        open={!!deleteId}
        title={t('anforderungen.deleteTitle', 'Anforderung löschen?')}
        message={t('anforderungen.deleteConfirm', 'Diese Anforderung wird endgültig entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Stack>
  );
}

function AddAnforderungRow({
  onCreate, statusLabel, nextNr, isVisible, kategorien,
}: {
  onCreate: (core: CoreFields) => Promise<void>;
  statusLabel: (s: Anforderung['status']) => string;
  nextNr: number;
  isVisible: (k: string) => boolean;
  kategorien: string[];
}) {
  const { t } = useTranslation('projects');
  const EMPTY: CoreFields = { role: '', ziel: '', kategorie: '', story_points: '', status: 'Neu' };
  const [form, setForm] = useState<CoreFields>(EMPTY);
  const [busy, setBusy] = useState(false);
  const canCreate = form.ziel.trim().length > 0;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate(form); setForm(EMPTY); } finally { setBusy(false); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell />
      <TableCell align="right">
        <Typography variant="body2" sx={{ color: 'var(--pm-fg-subtle)' }}>{nextNr}</Typography>
      </TableCell>
      {isVisible('role') && (
        <TableCell>
          <TextField variant="standard" fullWidth placeholder={t('anforderungen.role', 'Rolle')} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('ziel') && (
        <TableCell>
          <TextField variant="standard" fullWidth autoFocus placeholder={t('anforderungen.zielPlaceholder', 'Ziel eingeben…')} value={form.ziel} onChange={(e) => setForm({ ...form, ziel: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('kategorie') && (
        <TableCell>
          <Select variant="standard" fullWidth displayEmpty value={form.kategorie} onChange={(e) => setForm({ ...form, kategorie: e.target.value })}>
            <MenuItem value="">—</MenuItem>
            {kategorien.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('story_points') && (
        <TableCell align="right">
          <TextField variant="standard" type="number" sx={{ width: 56 }} value={form.story_points} onChange={(e) => setForm({ ...form, story_points: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('status') && (
        <TableCell>
          <Select variant="standard" fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Anforderung['status'] })}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('anforderungen.add', 'Neue Anforderung')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function AnforderungRow({
  item, pos, dragDisabled, onSave, onEditDetail, onDelete, statusLabel, isVisible, kategorien,
}: {
  item: Anforderung;
  pos: number;
  dragDisabled: boolean;
  onSave: (core: CoreFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
  statusLabel: (s: Anforderung['status']) => string;
  isVisible: (k: string) => boolean;
  kategorien: string[];
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id, disabled: dragDisabled });

  const [form, setForm] = useState<CoreFields>(coreFrom(item));
  const autosave = useAutosave<CoreFields>({
    canSave: (f) => f.ziel.trim().length > 0,
    onSave: async (f) => { await onSave(f); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm(coreFrom(item));
  }, [autosave.isDirty, item.role, item.ziel, item.kategorie, item.story_points, item.status]);

  const update = (patch: Partial<CoreFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);
  // Aktuellen (evtl. nicht mehr gelisteten) Kategorie-Wert als Option behalten.
  const kategorieOptions = form.kategorie && !kategorien.includes(form.kategorie)
    ? [form.kategorie, ...kategorien] : kategorien;

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <TableRow hover ref={setNodeRef} style={rowStyle}>
      <TableCell sx={{ width: 36, px: 0.5 }}>
        <Tooltip title={dragDisabled
          ? t('anforderungen.dragDisabled', 'Sortierung/Filter aktiv — Reihenfolge nicht änderbar')
          : t('anforderungen.dragMove', 'Zum Sortieren ziehen')}>
          <span>
            <IconButton
              size="small"
              disableRipple
              disabled={dragDisabled}
              sx={{ cursor: dragDisabled ? 'not-allowed' : 'grab', color: 'var(--pm-fg-subtle)' }}
              {...attributes}
              {...listeners}
              aria-label={t('anforderungen.dragMove', 'Zum Sortieren ziehen')}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--pm-fg-base)' }}>{pos}</Typography>
      </TableCell>
      {isVisible('role') && (
        <TableCell>
          <TextField variant="standard" fullWidth value={form.role} placeholder={t('anforderungen.role', 'Rolle')} onChange={(e) => update({ role: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('ziel') && (
        <TableCell>
          {/* multiline: lange Ziele umbrechen statt abzuschneiden (PHQ-80) */}
          <TextField variant="standard" fullWidth multiline value={form.ziel} onChange={(e) => update({ ziel: e.target.value })} onKeyDown={onKeyDown} />
          {item.nutzen && <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.nutzen}</Typography>}
        </TableCell>
      )}
      {isVisible('kategorie') && (
        <TableCell>
          <Select variant="standard" fullWidth displayEmpty value={form.kategorie} onChange={(e) => update({ kategorie: e.target.value })}>
            <MenuItem value="">—</MenuItem>
            {kategorieOptions.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      {isVisible('story_points') && (
        <TableCell align="right">
          <TextField variant="standard" type="number" sx={{ width: 56 }} value={form.story_points} onChange={(e) => update({ story_points: e.target.value })} onKeyDown={onKeyDown} />
        </TableCell>
      )}
      {isVisible('status') && (
        <TableCell>
          <Select variant="standard" fullWidth value={form.status} onChange={(e) => update({ status: e.target.value as Anforderung['status'] })}>
            {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
          </Select>
        </TableCell>
      )}
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
          <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
